export type DeliveryWindow = "morning" | "midday" | "afternoon" | "evening"

export type DeliveryStatus =
  | "scheduled"
  | "preparing"
  | "out_for_delivery"
  | "delivered"
  | "cancelled"

export type OrderStatus = "confirmed" | "fulfilled" | "cancelled"

export type ProductKind = "ingredient" | "packaging" | "finished_good" | "kit"

export type Address = {
  street?: string
  street2?: string
  city?: string
  state?: string
  zip?: string
  phone?: string
}

export type OrderLine = {
  id: number
  sku: string
  name: string
  quantity: number
  priceUnit: number
  priceSubtotal: number
  deliveryDate: string | null
  mealTime: string | null
  modifiers?: Array<{ attribute: string; values: string[] }>
}

export type Delivery = {
  date: string
  pickingId: number | null
  state: string
  window: string | null
  address?: Address
  lines: OrderLine[]
}

export type Order = {
  id: number
  name: string
  state: string
  amountTotal: number
  currency: string
  weekStartDate: string | null
  weekEndDate: string | null
  note: string | null
  deliveries: Delivery[]
  lines: OrderLine[]
  customerId?: number
  customerName?: string
  customerEmail?: string | null
  customerPhone?: string | null
}

export type Customer = {
  id: number
  supabaseUserId: string
  name: string
  email: string | null
  phone: string | null
  lastAddress?: Address | null
  orders?: Array<{
    id: number
    name: string
    state: string
    amountTotal: number
    currency: string
    weekStartDate: string | null
    weekEndDate: string | null
  }>
}

export type KitchenDayDelivery = {
  orderId: number
  orderName: string
  deliveryId: number
  date: string
  state: string
  window: string | null
  address: Address | null
  customer: { id: number; name: string; email: string | null; phone: string | null }
  lineCount: number
  allergens: string[]
}

export type PrepItem = {
  productId: number
  sku: string
  name: string
  quantity: number
  unit: string
  qtyOnHand: number
  shortfall: number
}

export type PackingStop = {
  orderId: number
  orderName: string
  deliveryId: number
  state: string
  window: string | null
  address: Address | null
  customer: { id: number; name: string; email: string | null; phone: string | null }
  lines: OrderLine[]
  allergens: string[]
}

export type WorkOrder = {
  kind: "work_order"
  orderId: number
  orderName: string
  date: string
  window: string | null
  address: Address | null
  note: string | null
  customer: { id: number; name: string; email: string | null; phone: string | null }
  meals: Array<{ mealTime: string; lines: OrderLine[] }>
  components: Array<{ productId: number; sku: string; name: string; quantity: number; unit: string }>
  allergens: string[]
}

export type Invoice = {
  kind: "invoice"
  disclaimer: string
  issuedAt: string
  orderId: number
  orderName: string
  state: string
  weekStartDate: string | null
  weekEndDate: string | null
  note: string | null
  currency: string
  amountTotal: number
  customer: { id: number; name: string; email: string | null; phone: string | null }
  lines: OrderLine[]
  deliveries: Array<{ date: string; window: string | null; state: string; amount: number }>
}

export type AdminProduct = {
  id: number
  sku: string
  slug: string
  name: string
  kind: ProductKind
  categoryId: number | null
  categoryName: string
  price: number
  currency: string
  active: boolean
  saleOk: boolean
  soldOut: boolean
  tracking: "none" | "lot"
  allergens: string[]
  dietaryTags: string[]
  variantCount: number
  imageUrlLight: string | null
  imageUrlDark: string | null
}

export type BomLine = {
  id: number
  componentProductId: number
  sku: string
  name: string
  quantity: number
  unit: string
}

export type AdminBom = {
  id: number
  productId: number | null
  variantId: number | null
  bomType: string
  lines: BomLine[]
}

export type ProductVariant = {
  id: number
  sku: string
  name: string
  price: number
  soldOut: boolean
  isDefault: boolean
  optionPairs: Array<{ attribute: string; name: string }>
  bom: AdminBom | null
}

export type AdminProductDetail = {
  product: AdminProduct & {
    description: string | null
    servingSize: string | null
    unitCost: number | null
    purchaseUnit: string | null
    bomType: "normal" | "phantom"
    recipeYield: number
    recipeYieldUnit: string
    nutrition: {
      servingBasis: string | null
      calories: number | null
      proteinGrams: number | null
      carbohydrateGrams: number | null
      fatGrams: number | null
    }
    availability: {
      active: boolean
      days: string[]
      startTime: string | null
      endTime: string | null
    }
    imageUrlLight: string | null
    imageUrlDark: string | null
  }
  variants: ProductVariant[]
  productBom: AdminBom | null
  bom: AdminBom | null
}

export type StockBalance = {
  productId: number
  sku: string
  name: string
  lotId: number | null
  qtyOnHand: number
  qtyReserved: number
}

export type Lot = {
  id: number
  productId: number
  sku: string
  lotCode: string
  qtyRemaining: number
  expiresOn: string | null
}

export type Category = {
  id: number
  name: string
  parentId: number | null
  kind: "section" | "leaf"
}

export const DELIVERY_STATUS_LABELS: Record<DeliveryStatus, string> = {
  scheduled: "Scheduled",
  preparing: "Preparing",
  out_for_delivery: "Out for delivery",
  delivered: "Delivered",
  cancelled: "Cancelled",
}

export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  confirmed: "Confirmed",
  fulfilled: "Complete",
  cancelled: "Cancelled",
}

export const PRODUCT_KIND_LABELS: Record<ProductKind, string> = {
  finished_good: "Menu",
  ingredient: "Ingredient",
  packaging: "Packaging",
  kit: "Kit",
}

export function nextDeliveryStatus(status: string): DeliveryStatus | null {
  if (status === "scheduled") return "preparing"
  if (status === "preparing") return "out_for_delivery"
  if (status === "out_for_delivery") return "delivered"
  return null
}
